import { Marked, marked, MarkedExtension } from 'marked';
import { stripInlineHandlers } from '../admin-core/utils/strip-inline-handlers';
import { environment } from '../../environments/environment';

/**
 * The fence tag the Pebbl email agent wraps its internal "what this draft could
 * not carry" note in — `not-in-draft`, exactly: lowercase, hyphenated, always
 * last in the body, one Markdown bullet per gap and no heading inside.
 *
 * It replaced a `**Not in this draft:**` prose heading the model wrote
 * inconsistently (sometimes bold, sometimes not, sometimes with a trailing
 * colon), which made it unreliable to pattern-match. The tag is now the only
 * marker and the LABEL IS OURS to supply — so the note has to be recognised
 * here, or it reaches the salesperson as unlabelled bullets that read like part
 * of the email.
 *
 * The note is written for the salesperson and must never reach the customer.
 * Drafts written before the rollout still carry the old prose form; those render
 * as ordinary body text, exactly as they always did.
 */
const NOT_IN_DRAFT_FENCE = 'not-in-draft';

/** The label and hint we put above the note's bullets, on every surface. */
const NOT_IN_DRAFT_LABEL = 'Not in this draft';
const NOT_IN_DRAFT_HINT = 'internal - delete before sending';

/**
 * A closing fence the model mistyped — two backticks instead of three, measured
 * on 1 note in 12 — is not read as a fence, so the block runs to the end of the
 * body and the stray marks land inside the last bullet. Strip a trailing
 * mark-only line from the note's content.
 */
const STRAY_FENCE = /\n[ \t]*(?:`+|~+)[ \t]*$/;

/** Matches an opening/closing fence line, capturing indent, marker and info string. */
const FENCE_LINE = /^(\s*)(`{3,}|~{3,})[ \t]*(.*)$/;

/**
 * Make every rendered markdown link open in a new tab (target="_blank") with
 * rel="noopener noreferrer".
 *
 * The rendered HTML is trusted downstream (bypassSecurityTrustHtml), so we only
 * linkify safe URL schemes and encode the quote/angle characters in the href —
 * this prevents javascript:/data: hrefs and attribute-breakout injection.
 *
 * A factory, not a shared constant: `marked.use` binds each override to the
 * renderer it patches, so two pipelines must not be handed the same function
 * objects.
 */
function safeLinkExtension(): MarkedExtension {
  return {
    renderer: {
      link(token) {
        const text = this.parser.parseInline(token.tokens);
        const href = (token.href || '').trim();
        const isSafe = /^(https?:|mailto:|tel:|#|\/|\.)/i.test(href);
        if (!isSafe) {
          return text;
        }
        const safeHref = href.replace(/"/g, '%22').replace(/</g, '%3C').replace(/>/g, '%3E');
        // Pebbl titles the recommended product's link `"recommended"` — a marker for parsers,
        // not text for people (the badge comes from the product_cards frame), so it is not
        // carried into a hover tooltip.
        const title = /^\s*recommended\s*$/i.test(token.title || '') ? '' : token.title;
        const titleAttr = title ? ` title="${title.replace(/"/g, '&quot;')}"` : '';
        return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
      },
    },
  };
}

/**
 * Turn a ```not-in-draft fence into the internal note's own bordered block
 * instead of a monospace code card. Returning `false` for every other info
 * string hands the token back to marked's own code renderer, so a ```sql block
 * is untouched.
 *
 * Replacing the `code` renderer is all that is needed here: unlike
 * react-markdown, marked hands the whole `<pre><code>` to this one override, so
 * there is no wrapping `<pre>` left behind to fight the block's own layout.
 */
function notInDraftExtension(): MarkedExtension {
  return {
    renderer: {
      code(token) {
        if ((token.lang || '').trim().toLowerCase() !== NOT_IN_DRAFT_FENCE) {
          return false;
        }
        return notInDraftCardHtml(token.text);
      },
    },
  };
}

let markedReady = false;

function configureMarked(): void {
  if (markedReady) {
    return;
  }
  markedReady = true;
  marked.use(safeLinkExtension(), notInDraftExtension());
}

configureMarked();

/**
 * Pipeline for the note's OWN Markdown. The fence hands us raw text, so the
 * bullets need a parse of their own to come out as a list.
 *
 * Deliberately a separate `Marked` instance: marked's `Parser` assigns itself to
 * `renderer.parser`, so re-entering the shared singleton from inside a render
 * would repoint the outer parse's renderer at the inner parser. Its own instance
 * cannot collide — and it carries no fence override, so a note cannot nest.
 */
let notePipeline: Marked | null = null;

function parseNoteMarkdown(src: string): string {
  if (!notePipeline) {
    notePipeline = new Marked(safeLinkExtension());
  }
  return notePipeline.parse(src.replace(STRAY_FENCE, ''), { async: false }) as string;
}

/** The note as a self-contained block: our label and hint, then its bullets. */
function notInDraftCardHtml(text: string): string {
  const body = parseNoteMarkdown((text || '').replace(/\n$/, ''));
  return (
    '<div class="not-in-draft" role="note">' +
    `<div class="not-in-draft__head">${NOT_IN_DRAFT_LABEL}` +
    `<span class="not-in-draft__hint">${NOT_IN_DRAFT_HINT}</span></div>` +
    `<div class="not-in-draft__body markdown">${body}</div>` +
    '</div>'
  );
}

/** An email draft split into the reply itself and its internal note. */
export interface DraftNoteSplit {
  /** The reply body, with the internal note taken out. */
  body: string;
  /** The note's own Markdown (bullets only), or `''` when the draft has none. */
  note: string;
}

/**
 * Split a `not-in-draft` note off an email draft body.
 *
 * For hosts that put the draft into an editor rather than rendering it
 * read-only: the note is NOT part of the email, so it must not be pasted into a
 * body that can be sent. Callers render the returned `note` beside the editor
 * (see `renderNotInDraftToHtml`) and paste only `body`.
 *
 * Line-based rather than one regex, for the same reason the rest of this file
 * is: fence state has to be tracked so a `not-in-draft` line INSIDE another
 * fenced block is left alone. Tolerates both of the ways the model gets the
 * close wrong — a mistyped two-mark fence, and no closing fence at all, which
 * runs the note to the end of the body. There is never an empty note: when
 * nothing was missing the fence is simply absent, and `note` comes back `''`.
 */
export function extractNotInDraft(text: string): DraftNoteSplit {
  const src = (text || '').replace(/\r\n/g, '\n');
  // The tag is the only marker, so its absence is a definitive "no note" —
  // worth short-circuiting, since every draft goes through here.
  if (!src.includes(NOT_IN_DRAFT_FENCE)) {
    return { body: src, note: '' };
  }

  const body: string[] = [];
  const note: string[] = [];
  let inNote = false;
  let inCode = false;
  let codeMarker = '';

  for (const line of src.split('\n')) {
    const fence = line.match(FENCE_LINE);

    if (inNote) {
      // Anything that is nothing but fence marks closes the note. Two marks
      // count as well as three — that mistyped close is the one the model
      // actually produces, and reading it as content would leave the stray
      // backticks in the last bullet.
      if (/^[ \t]*(?:`{2,}|~{2,})[ \t]*$/.test(line)) {
        inNote = false;
        continue;
      }
      note.push(line);
      continue;
    }

    if (inCode) {
      body.push(line);
      if (fence && line.trim().startsWith(codeMarker)) {
        inCode = false;
        codeMarker = '';
      }
      continue;
    }

    if (fence) {
      if (fence[3].trim().toLowerCase() === NOT_IN_DRAFT_FENCE) {
        inNote = true;
        continue;
      }
      body.push(line);
      inCode = true;
      codeMarker = fence[2];
      continue;
    }

    body.push(line);
  }

  return {
    // The note sits after the sign-off, so taking it out leaves the blank line
    // that separated the two — the reply must not end on it.
    body: body.join('\n').replace(/\s+$/, ''),
    note: note.join('\n').replace(STRAY_FENCE, '').trim(),
  };
}

/**
 * Render a `not-in-draft` note's Markdown for a host that supplies its own label
 * and chrome — the composers' internal-note panel, which owns the heading
 * because the note lives OUTSIDE the editor there. Empty in, empty out.
 */
export function renderNotInDraftToHtml(note: string): string {
  const src = (note || '').trim();
  if (!src) {
    return '';
  }
  return stripInlineHandlers(parseNoteMarkdown(src));
}

/**
 * Strip the shared leading indentation from each blank-line-separated block.
 *
 * Pebbl replies sometimes arrive with a body indented by several spaces.
 * CommonMark treats a 4+ space indent as an *indented code block*, so `marked`
 * would render the block verbatim in a monospaced <pre><code> with the markdown
 * (**bold**, `*` bullets, links) showing as raw characters. Dedenting fixes
 * that. Fenced code blocks (``` … ```) are passed through untouched so
 * intentional code — including code with blank lines — is preserved exactly.
 */
function dedentBlocks(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  let block: string[] = [];
  let inFence = false;
  let fenceMarker = '';

  const flush = (): void => {
    if (block.length === 0) {
      return;
    }
    let min = Infinity;
    for (const line of block) {
      if (line.trim() === '') {
        continue;
      }
      const indent = (line.match(/^[ \t]*/) as RegExpMatchArray)[0].length;
      min = Math.min(min, indent);
    }
    if (Number.isFinite(min) && min > 0) {
      for (const line of block) {
        out.push(line.slice(min));
      }
    } else {
      out.push(...block);
    }
    block = [];
  };

  for (const line of lines) {
    const fence = line.match(/^\s*(`{3,}|~{3,})/);
    if (inFence) {
      out.push(line);
      if (fence && line.trim().startsWith(fenceMarker)) {
        inFence = false;
        fenceMarker = '';
      }
      continue;
    }
    if (fence) {
      flush();
      inFence = true;
      fenceMarker = fence[1];
      out.push(line);
      continue;
    }
    if (line.trim() === '') {
      flush();
      out.push(line);
    } else {
      block.push(line);
    }
  }
  flush();
  return out.join('\n');
}

/**
 * Wrap every rendered `<table>` in a scrollable container so wide/long markdown
 * tables get a bounded, rounded, scrollable card (matching the structured
 * `.table-scroll` component) instead of overflowing the message inline.
 */
function wrapMarkdownTables(html: string): string {
  return html.replace(/<table[\s\S]*?<\/table>/g, (table) => `<div class="md-table-scroll">${table}</div>`);
}

/**
 * Append the ↗ that marks a reply link as opening in a new tab, after every link in a chat
 * answer - the storefront widget's `withLinkArrow()` (gatherco-frontend-v2
 * `core/utils/markdown.ts`), ported. The glyph is drawn by CSS (`.md-link-arrow`, ai.styles.css);
 * this only places the empty span it hangs off.
 *
 * The arrow is tied to the label's last word with a nowrap span, so it never wraps onto a line
 * of its own under the link. A long last word (a bare URL as the label) or one ending in markup
 * is left out of the span: nowrap there would stop the link wrapping at all.
 *
 * Chat prose only: an email draft is copy for the customer and must not carry the span.
 */
const LINK_ARROW = '<span class="md-link-arrow" aria-hidden="true"></span>';
const MAX_LINK_TAIL_LENGTH = 24;

function withLinkArrows(html: string): string {
  return html.replace(/(<a\b[^>]*>)([\s\S]*?)<\/a>/g, (_match, open: string, label: string) => {
    const lastWord = /([^\s<>]+)$/.exec(label);
    if (!lastWord || lastWord[1].length > MAX_LINK_TAIL_LENGTH) {
      return `${open}${label}${LINK_ARROW}</a>`;
    }
    return (
      `${open}${label.slice(0, lastWord.index)}` +
      `<span class="md-link-tail">${lastWord[1]}${LINK_ARROW}</span></a>`
    );
  });
}

/** What follows a product name rather than a bolded word: a comma, full stop or dash. */
const NAME_FOLLOWER = /^(?:[,.]|\s+[-\u2013\u2014]\s)/;

/**
 * Link the bold name a list item opens on - `**MAPLE MOON Coping**, 600 x 400 x 28...` - to a
 * storefront product search for it, so it reads and works like the linked names beside it.
 * The storefront widget's `linkItemNames()` (gatherco-frontend-v2 `core/utils/markdown.ts`),
 * ported, so a transcript shows the links the customer saw.
 *
 * The agent links a product where it has its page and bolds the name where it has none. Only a
 * name counts: a bold run with a colon is a label ("**Note:** ..."), and a bold run with a list
 * nested straight under it is a section heading. Otherwise it counts where a sibling item opens
 * on a link, or where the name is followed by a comma, full stop or dash, or ends the item - a
 * bold word opening a sentence ("**Always** seal...") is none of those.
 *
 * Marked `md-search-link` so placeProductCards() never takes it for a product page.
 */
function linkItemNames(html: string): string {
  if (typeof document === 'undefined' || !/<li>/.test(html)) return html;
  const template = document.createElement('template');
  template.innerHTML = html;
  const base = (environment.frontendHostUrl || 'https://www.gatherco.com.au').replace(/\/+$/, '');
  const leadOf = (item: Element): ChildNode | null => {
    let node = item.firstChild;
    while (node && node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) node = node.nextSibling;
    // A loose list wraps the item's text in a <p>.
    return node instanceof Element && node.tagName === 'P' ? leadOf(node) : node;
  };
  const opensOnLink = (item: Element): boolean => {
    let node = leadOf(item);
    while (node instanceof Element && /^(STRONG|EM)$/.test(node.tagName)) node = node.firstChild;
    return node instanceof Element && node.tagName === 'A';
  };
  let changed = false;
  template.content.querySelectorAll('ul, ol').forEach((list) => {
    const items = Array.from(list.children).filter((child) => child.tagName === 'LI');
    const hasLinkedName = items.some(opensOnLink);
    for (const item of items) {
      const bold = leadOf(item);
      if (!(bold instanceof Element) || bold.tagName !== 'STRONG' || bold.children.length) continue;
      const name = (bold.textContent ?? '').trim();
      if (!name || name.includes(':')) continue;
      let next = bold.nextSibling;
      while (next && next.nodeType === Node.TEXT_NODE && !next.textContent?.trim() && next.nextSibling) {
        next = next.nextSibling;
      }
      if (next instanceof Element && (next.tagName === 'UL' || next.tagName === 'OL')) continue;
      const endsItem = !next || next.nodeName === 'BR' || !next.textContent?.trim();
      const follower = next?.nodeType === Node.TEXT_NODE && NAME_FOLLOWER.test(next.textContent ?? '');
      if (!hasLinkedName && !endsItem && !follower) continue;
      const link = document.createElement('a');
      link.className = 'md-search-link';
      link.href = `${base}/search?q=${encodeURIComponent(name)}`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = name;
      bold.replaceChildren(link);
      changed = true;
    }
  });
  return changed ? template.innerHTML : html;
}

const EMAIL_FENCE_INFO = 'email';

/**
 * `breaks: true` - a single newline is a real `<br>`, as the storefront widget renders it. A
 * sign-off (`Kind regards,\n[Your name]\nGatherCo`) or an address block keeps its lines in
 * order wherever it lands; left as "\n" text it relied on the bubble's `white-space: pre-wrap`
 * and ran together into one line anywhere that does not reach (a product entry is `normal`).
 */
const CHAT_PARSE = { async: false, breaks: true } as const;

export type MessageSegmentKind = 'markdown' | 'email';

export interface MessageSegment {
  kind: MessageSegmentKind;
  /** Rendered HTML for the segment. */
  html: string;
}

/**
 * Split a Pebbl reply into ordered segments: ordinary markdown prose, and any
 * `email`-labelled fenced blocks (```email … ```). Pebbl wraps a drafted email
 * body in an `email` fence; callers render those as a dedicated "email draft"
 * card, while everything else renders as normal markdown. Any other fence
 * (```sql, ```json, plain ```) stays inside the surrounding markdown segment and
 * still renders as a code block.
 */
export function splitMessageSegments(text: string): MessageSegment[] {
  configureMarked();
  if (!text) {
    return [];
  }

  const lines = text.split('\n');
  const segments: MessageSegment[] = [];
  let mdBuf: string[] = [];
  let emailBuf: string[] = [];
  let inCodeFence = false;
  let codeMarker = '';
  let inEmail = false;
  let emailMarker = '';

  const renderMarkdownBuf = (): void => {
    const src = mdBuf.join('\n');
    mdBuf = [];
    if (src.trim() === '') {
      return;
    }
    segments.push({
      kind: 'markdown',
      html: stripInlineHandlers(
        withLinkArrows(wrapMarkdownTables(linkItemNames(marked.parse(dedentBlocks(src), CHAT_PARSE) as string))),
      ),
    });
  };
  const renderEmailBuf = (): void => {
    const src = dedentBlocks(emailBuf.join('\n'));
    emailBuf = [];
    // Wrap tables so a wide draft-email table scrolls horizontally instead of
    // being clipped by the email-draft card (overflow:hidden) — same treatment
    // markdown segments get. The email body carries the `.markdown` class, so the
    // `.md-table-scroll` overflow-x rule applies.
    segments.push({ kind: 'email', html: stripInlineHandlers(wrapMarkdownTables(marked.parse(src, CHAT_PARSE) as string)) });
  };

  for (const line of lines) {
    const fence = line.match(/^(\s*)(`{3,}|~{3,})[ \t]*(.*)$/);

    if (inEmail) {
      if (fence && line.trim().startsWith(emailMarker)) {
        renderEmailBuf();
        inEmail = false;
        emailMarker = '';
      } else {
        emailBuf.push(line);
      }
      continue;
    }

    if (inCodeFence) {
      mdBuf.push(line);
      if (fence && line.trim().startsWith(codeMarker)) {
        inCodeFence = false;
        codeMarker = '';
      }
      continue;
    }

    if (fence) {
      const marker = fence[2];
      const info = fence[3].trim().toLowerCase();
      if (info.startsWith(EMAIL_FENCE_INFO)) {
        renderMarkdownBuf();
        inEmail = true;
        emailMarker = marker;
      } else {
        mdBuf.push(line);
        inCodeFence = true;
        codeMarker = marker;
      }
      continue;
    }

    mdBuf.push(line);
  }

  // Flush whatever is still open (an unterminated email fence still streams into
  // a draft card; anything else is trailing markdown).
  if (inEmail) {
    renderEmailBuf();
  } else {
    renderMarkdownBuf();
  }
  return segments;
}

/**
 * Parse Pebbl markdown to a single HTML string. Email drafts are rendered inline
 * (formatted, not as a code block); surfaces that want a distinct email card use
 * `splitMessageSegments` instead.
 */
export function renderMarkdownToHtml(text: string): string {
  return splitMessageSegments(text || '')
    .map((segment) => segment.html)
    .join('');
}

/**
 * True when a Pebbl reply body is already HTML rather than Markdown.
 *
 * The email agent hands back a finished email body; depending on the model it is
 * either GitHub-flavoured Markdown or ready-made HTML. A single leading tag such
 * as `<p>` / `<div>` / `<br>` / `<table>` is enough to tell them apart — Markdown
 * prose never opens with one.
 */
function looksLikeHtml(text: string): boolean {
  return /<(?:p|div|br|ul|ol|li|table|thead|tbody|tr|td|th|h[1-6]|strong|em|b|i|a|blockquote|span|pre|code|hr|img)\b[^>]*>/i.test(
    text,
  );
}

/**
 * True when HTML already carries block-level structure of its own (paragraphs,
 * lists, tables, explicit `<br>` breaks). When it does NOT — inline tags such as
 * `<strong>`/`<a>` only — the model is leaning on blank lines for its paragraph
 * breaks, which HTML would collapse to a single space.
 */
function hasBlockStructure(html: string): boolean {
  return /<(?:p|div|br|ul|ol|li|table|tr|blockquote|h[1-6]|hr|pre)\b[^>]*>/i.test(html);
}

/**
 * Promote blank-line-separated, inline-only HTML into real paragraphs: split on
 * a blank line, turn remaining single newlines into `<br>`, and wrap each block
 * in `<p>`. Only used when {@link hasBlockStructure} is false, so we never
 * double up on markup the model already provided.
 */
function inlineHtmlToParagraphs(html: string): string {
  return html
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block !== '')
    .map((block) => `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Parse a Pebbl email draft's Markdown to HTML.
 *
 * Distinct from {@link renderMarkdownToHtml} (which the chat surfaces use) in two
 * ways that matter for an email body: `breaks: true` so a single newline becomes
 * a `<br>` — an address block or a `Kind regards,\nThe Team` sign-off must keep
 * its lines — and no `email`-fence splitting, because the draft body IS the
 * email, not a chat message that embeds one.
 */
function renderEmailMarkdown(src: string): string {
  configureMarked();
  const html = marked.parse(dedentBlocks(src), { async: false, breaks: true }) as string;
  // No `md-table-scroll` wrapper here (that's a chat-only scroll card): the draft
  // goes into Quill, which drops wrapper divs and rebuilds the table from its own
  // blots anyway. Header cells are normalised for Quill separately below.
  return stripInlineHandlers(html);
}

/**
 * Normalise a table into one clean row sequence the editor can style predictably.
 *
 * A markdown table arrives as `<thead><tr><th>…</thead><tbody><tr><td>…`, with
 * newline text nodes between the sections and rows. Two things then go wrong in
 * the composer:
 *
 *  - Header styling keyed on `tr:first-child` hits the first row of BOTH `<thead>`
 *    and `<tbody>`, so the real header AND the first data row both get shaded.
 *  - The inter-section/row whitespace text nodes get turned into a stray empty
 *    row (a blank shaded band under the header).
 *
 * So we flatten every table to a single `<tbody>`: convert `<th>`→`<td>` (Quill
 * only treats `<td>` as a cell — a `<th>` would merge the header row), keep the
 * rows in order with the header first, drop truly empty rows, and strip the
 * whitespace-only text nodes. The header is then unambiguously the first row,
 * for both the editor CSS and the inlined send styling. No-op without a table.
 */
function normalizeTableForEditor(html: string): string {
  if (!/<table[\s>]/i.test(html)) return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('table').forEach((table) => {
    table.querySelectorAll('th').forEach((th) => {
      const td = document.createElement('td');
      Array.from(th.attributes).forEach((attr) => td.setAttribute(attr.name, attr.value));
      td.innerHTML = th.innerHTML;
      th.replaceWith(td);
    });
    const tbody = document.createElement('tbody');
    Array.from(table.querySelectorAll('tr')).forEach((tr) => {
      if (!tr.querySelector('td')) return; // drop empty rows
      // Remove whitespace-only text nodes between cells so they can't become a
      // stray empty row/cell when the table is re-parsed.
      Array.from(tr.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE && !(node.textContent || '').trim()) {
          node.remove();
        }
      });
      tbody.appendChild(tr);
    });
    table.innerHTML = '';
    table.appendChild(tbody);
  });
  return container.innerHTML;
}

/**
 * Collapse an email draft's blank lines so a `\n\n` paragraph break shows as one
 * line break, not an empty line.
 *
 * The model writes prose as blank-line-separated paragraphs, which render as
 * adjacent `<p>` blocks. Quill's stylesheet zeroes `.ql-editor p` margins, so
 * `<p>A</p><p>B</p>` already sits on two consecutive lines — one break, which is
 * what a reply should look like. The only thing that can add an empty line on
 * top of that is a literal blank block (`<p><br></p>`, `<p></p>`, an empty
 * `<div>`), whether we inserted it or the model's own HTML carried it. So we
 * drop those, and nothing else: real blocks keep their order, their text and
 * their markup.
 *
 * Top-level children only — a blank `<p>` inside a table cell or list item is
 * that cell's own layout, not a paragraph break between blocks. Blocks holding
 * media (`img`/`hr`/`table`) are never blank, whatever their text says.
 *
 * Deliberately structural rather than a `\n{2,}` → `\n` pass over the source
 * text: collapsing the Markdown itself makes a list swallow the paragraph that
 * follows it (`Let me know` becomes a bullet) and a table swallow it as an extra
 * row, because a blank line is what ends those blocks in CommonMark.
 *
 * This also lines the editor up with what is actually delivered:
 * `EmailService.normalizeBodyHtml` strips exactly these empty blocks on send, so
 * the blank lines only ever existed while the draft was being reviewed.
 */
function collapseBlankBlocks(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = html;
  Array.from(container.children).forEach((el) => {
    if (el.tagName !== 'P' && el.tagName !== 'DIV') return;
    if (el.querySelector('img, hr, table')) return;
    // NBSP counts as blank: a "spacer" line the model wrote as `&nbsp;` is the
    // same empty line to the reader as an empty one.
    if ((el.textContent || '').replace(/\u00a0/g, ' ').trim() !== '') return;
    el.remove();
  });
  /* …then the tail. A draft that ends in bare `<br>`s is the model writing its
     own trailing blank line, and the pass above cannot see it: a `<br>` is
     neither a `P` nor a `DIV`. Quill turns each one into an empty paragraph, and
     because the signature preview is the editor's sibling those paragraphs land
     between the sign-off and the signature — the same unwanted gap a stored
     draft used to carry, arriving from the other direction.

     TRAILING only, and only at the top level: a `<br>` with anything after it is
     a line break inside the message (`Thanks,<br>Krupa`) and is exactly what
     `breaks: true` is here to preserve. */
  const isSpacer = (n: Node | null): boolean => !!n && (
    (n.nodeType === Node.TEXT_NODE && (n.textContent || '').trim() === '')
    || (n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName === 'BR')
  );
  const trimTail = (parent: Node): void => {
    while (isSpacer(parent.lastChild)) parent.removeChild(parent.lastChild as Node);
  };
  trimTail(container);
  /* …and inside the last block, where the same break costs the same blank line:
     `<p>Thanks,<br>Krupa<br><br></p>` is a real soft break followed by a
     spacer, and Quill keeps the spacer as a second break inside that paragraph.
     Only the last block, and only its tail — every other `<br>` in the draft,
     this one's own `Thanks,<br>Krupa` included, is untouched. */
  if (container.lastElementChild) trimTail(container.lastElementChild);
  return container.innerHTML;
}

/**
 * Render a Pebbl-written email draft for the reply editor.
 *
 * Pebbl may return the body as either Markdown or ready-made HTML. Running HTML
 * back through the Markdown parser is lossy: an indented tag is treated as a
 * CommonMark indented code block and comes out escaped as literal
 * `&lt;p&gt;…` text in a `<pre><code>` card. So when the body already looks like
 * HTML we keep the model's own markup (only stripping inline event handlers, per
 * `stripInlineHandlers`); if that HTML is inline-only, we first rebuild its
 * paragraphs from the blank lines it leaned on. Plain Markdown is rendered with
 * `breaks: true`, so a single newline inside a paragraph keeps its own line —
 * an address block or a `Kind regards,\nThe Team` sign-off must not reflow.
 *
 * Either way we finish with {@link collapseBlankBlocks}, so the model's `\n\n`
 * arrives in the editor as ONE line break: paragraph blocks stay separate blocks
 * (lists and tables keep their structure) but no empty line is left between them.
 *
 * The internal `not-in-draft` note is dropped here rather than rendered. It is
 * not part of the email and nothing downstream removes it before the reply goes
 * out, so it must never become editor content that Send could pick up. Hosts
 * take it with {@link extractNotInDraft} and show it beside the editor instead —
 * dropping it here as well means no path can paste it into a sendable body.
 */
export function renderEmailDraftToHtml(text: string): string {
  const src = extractNotInDraft(text).body;
  let html: string;
  if (looksLikeHtml(src)) {
    html = stripInlineHandlers(src);
    if (!hasBlockStructure(html)) {
      html = inlineHtmlToParagraphs(html);
    }
  } else {
    html = renderEmailMarkdown(src);
  }
  html = normalizeTableForEditor(html);
  return collapseBlankBlocks(html);
}
